import CategoryTree from "components/layouts/MainLayout/components/HeaderBottomBar/components/CategoryTree/CategoryTree";
import { useSelector } from "store";
import { useGetCmsSectionArticle } from "api/endpoints/useGetCmsSectionArticle";
import Popover from "@mui/material/Popover";
import { useLocation, useNavigate } from "react-router-dom";
// główny layout - dolna belka nagłówka

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { ChevronDown, PatchCheck, PlusCircle, DashCircle, List } from 'react-bootstrap-icons';
import classnames from 'classnames';
import { useGetLayoutCategories } from 'api';
import { ICategoryListItem } from 'api/types';
import { useClickOutside, useAppNavigate } from 'hooks';
import { Container, Button, Link, Loader } from 'components/controls';
import styles from 'theme/components/layouts/MainLayout/components/HeaderBottomBar/HeaderBottomBar.module.scss';
const HeaderBottomBar = () => {
  const location = useLocation();
  const navigateRouter = useNavigate();
  const {
    i18n
  } = useTranslation();
  const {
    overloginUserId,
    token
  } = useSelector(state => state.auth);
  const [scrollTop, setScrollTop] = useState(0);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const isHomePage = location.pathname.split("/").filter(item => item).length < 2;

  // Pobieranie HTML z CMS
  const {
    data: navigationBarData,
    refetch: refetchNavigationBar
  } = useGetCmsSectionArticle("HEADER_NAVIGATION", "HEADER_NAVIGATION_BAR", {
    enabled: false
  });
  useEffect(() => {
    refetchNavigationBar();
  }, [refetchNavigationBar, overloginUserId, token]);

  // Funkcja do wyciągnięcia HTML z artykułu CMS
  const getNavigationBarHtml = useCallback(() => {
    if (!navigationBarData?.article_fields) return null;
    const htmlField = navigationBarData.article_fields.find(field => field.type === "HTMLBLOCK");

    // HTML może być w value (jako string) lub jako tablica tłumaczeń
    if (htmlField?.value) {
      // Jeśli value jest tablicą tłumaczeń, wybierz aktualne tłumaczenie
      if (Array.isArray(htmlField.value)) {
        const currentLang = navigationBarData.language || 'pl';
        const translation = htmlField.value.find((t: any) => t.language === currentLang);
        return translation?.translation || translation?.value || htmlField.value[0]?.translation || htmlField.value[0]?.value || null;
      }
      // Jeśli value jest stringiem
      if (typeof htmlField.value === 'string') {
        return htmlField.value;
      }
    }
    return null;
  }, [navigationBarData]);
  const navigationBarHtml = getNavigationBarHtml();

  // Obsługa kliknięć w linki nawigacyjne z CMS oraz hover dla dropdown
  useEffect(() => {
    if (!navigationBarHtml) return;
    const closeAllDropdowns = () => {
      document.querySelectorAll('.dropdownMenu.open').forEach(menu => {
        menu.classList.remove('open');
      });
      document.querySelectorAll('.dropdownWrapper.open').forEach(wrapper => {
        wrapper.classList.remove('open');
      });
    };
    const cleanupFunctions: Array<() => void> = [];

    // Czekamy, aż HTML zostanie wyrenderowany
    const timer = setTimeout(() => {
      // Obsługa hover dla każdego dropdown
      const dropdownWrappers = document.querySelectorAll('.dropdownWrapper');
      dropdownWrappers.forEach(wrapper => {
        const handleMouseEnter = () => {
          closeAllDropdowns();
          const dropdownId = wrapper.querySelector('a[data-dropdown-toggle]')?.getAttribute('data-dropdown-toggle');
          if (dropdownId) {
            const dropdownMenu = document.getElementById(dropdownId);
            if (dropdownMenu) {
              dropdownMenu.classList.add('open');
              wrapper.classList.add('open');
            }
          }
        };
        const handleMouseLeave = (event: MouseEvent) => {
          const relatedTarget = event.relatedTarget as HTMLElement;
          if (relatedTarget && !wrapper.contains(relatedTarget)) {
            const dropdownId = wrapper.querySelector('a[data-dropdown-toggle]')?.getAttribute('data-dropdown-toggle');
            if (dropdownId) {
              const dropdownMenu = document.getElementById(dropdownId);
              if (dropdownMenu && !dropdownMenu.contains(relatedTarget)) {
                dropdownMenu.classList.remove('open');
                wrapper.classList.remove('open');
              }
            }
          }
        };
        wrapper.addEventListener('mouseenter', handleMouseEnter);
        wrapper.addEventListener('mouseleave', handleMouseLeave);
        cleanupFunctions.push(() => {
          wrapper.removeEventListener('mouseenter', handleMouseEnter);
          wrapper.removeEventListener('mouseleave', handleMouseLeave);
        });

        // Dodaj też listener na menu, aby nie zamykać gdy mysz jest nad menu
        const dropdownId = wrapper.querySelector('a[data-dropdown-toggle]')?.getAttribute('data-dropdown-toggle');
        if (dropdownId) {
          const dropdownMenu = document.getElementById(dropdownId);
          if (dropdownMenu) {
            const handleMenuLeave = (event: MouseEvent) => {
              const relatedTarget = event.relatedTarget as HTMLElement;
              if (relatedTarget && !wrapper.contains(relatedTarget)) {
                dropdownMenu.classList.remove('open');
                wrapper.classList.remove('open');
              }
            };
            dropdownMenu.addEventListener('mouseleave', handleMenuLeave);
            cleanupFunctions.push(() => {
              dropdownMenu.removeEventListener('mouseleave', handleMenuLeave);
            });
          }
        }
      });

      // Obsługa kliknięcia w link nawigacyjny
      const handleLinkClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const link = target.closest('a[data-nav-link]') as HTMLAnchorElement;
        if (link && !link.hasAttribute('data-dropdown-toggle')) {
          event.preventDefault();
          const path = link.getAttribute('data-nav-link');
          if (path && navigateRouter && path !== '#') {
            closeAllDropdowns();
            navigateRouter(`/${i18n.language}${path}`);
          }
        }
      };
      const container = document.querySelector('.StylePath-Components-Layouts-MainLayout-Components-HeaderBottomBar');
      if (container) {
        container.addEventListener('click', handleLinkClick);
        cleanupFunctions.push(() => {
          container.removeEventListener('click', handleLinkClick);
        });
      }
    }, 100);
    return () => {
      clearTimeout(timer);
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [navigationBarHtml, navigateRouter, i18n.language]);

  // śledzenie scrollTop z elementu scrollableContent
  useEffect(() => {
    const scrollableElement = document.querySelector('.scrollableContent');
    if (!scrollableElement) {
      // Jeśli nie ma elementu scrollableContent, używamy window (dla innych stron)
      const handleScroll = () => {
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        setScrollTop(currentScrollTop);
      };
      handleScroll();
      window.addEventListener('scroll', handleScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
    const handleScroll = () => {
      const currentScrollTop = scrollableElement.scrollTop || 0;
      setScrollTop(currentScrollTop);
    };

    // Sprawdź od razu
    handleScroll();

    // Dodaj nasłuchiwanie na scroll elementu scrollableContent
    scrollableElement.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      scrollableElement.removeEventListener('scroll', handleScroll);
    };
  }, [isHomePage]);

  // aktualizacja stanu isButtonDisabled gdy zmienia się scrollTop lub isHomePage
  useEffect(() => {
    // na stronie głównej: przycisk nieaktywny gdy scrollTop <= 550px, aktywny gdy > 550px
    // na innych stronach: przycisk zawsze aktywny
    setIsButtonDisabled(isHomePage && scrollTop <= 550);
  }, [isHomePage, scrollTop]);
  const {
    t
  } = useTranslation();
  const navigate = useAppNavigate();

  // czy rozwinięty jest panel z kategoriami
  const [isPopupOpened, setIsPopupOpened] = useState(false);

  // panel z kategoriami
  const popupContentRef = useRef<HTMLDivElement>(null);

  // przycisk rozwijający panel z kategoriami
  const triggerRef = useRef<HTMLDivElement>(null);

  // zamykanięcie panelu z kategoriami po kliknięciu na zewnątrz
  useClickOutside([popupContentRef, triggerRef], () => setIsPopupOpened(false));

  // lista ID'ków rozwiniętych kategorii
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<number[]>([]);

  // pobranie listy kategorii
  const {
    data: categoriesData,
    isLoading: isCategoriesLoading
  } = useGetLayoutCategories({
    page: 1,
    limit: 999,
    search_keyword: ''
  });

  // funkcja renderująca drzewo kategorii
  const renderCategoryTree = useCallback((category: ICategoryListItem) => {
    // funkcja rekurencyjna renderująca  listę wraz z rozwiniętymi podkategoriami
    const renderTree = (categories: ICategoryListItem[] = [], level = 0) => categories.map(category => <div key={category.id} className={classnames(styles.category, {
      [styles.main]: level === 0
    })}>
            {category.subcategories_total_count > 0 && <>
                {expandedCategoryIds.includes(category.id) || category.subcategories.some(item => item.id === undefined) ? <DashCircle onClick={() => setExpandedCategoryIds(prevState => prevState.filter(categoryId => categoryId !== category.id))} /> : <PlusCircle onClick={() => setExpandedCategoryIds(prevState => [...prevState, category.id])} />}
              </>}
            <Link to={`/${category.url_link}`} onClick={() => {
        setIsPopupOpened(false);
      }} className={classnames({
        [styles.chosen]: category.id === undefined
      })}>
              {category.name} <span className={styles.counter}>({category.products_count})</span>
            </Link>

            {category.subcategories_total_count > 0 && (expandedCategoryIds.includes(category.id) || category.subcategories.some(item => item.id === undefined)) && renderTree(category.subcategories, level + 1)}
          </div>);
    return renderTree([category]);
  }, [expandedCategoryIds, undefined]);
  return <div className={classnames(styles.componentWrapper, 'StylePath-Components-Layouts-MainLayout-Components-HeaderBottomBar')}>
      <Container>
        <div className={styles.bottomBar}>
          <div>
            <span ref={triggerRef} className={styles.categoriesWrapper}>
              <button onClick={isButtonDisabled ? undefined : () => setIsPopupOpened(!isPopupOpened)} className={classnames(styles.categoryButton, {
              [styles.disabled]: isButtonDisabled
            })} disabled={isButtonDisabled}>
                <span className={styles.hamburgerIcon}>
                  <List />
                </span>
                <span className={styles.categoryButtonText}>
                  {t('Wybierz kategorie')}
                  <span className={styles.arrowDown}></span>
                </span>
              </button>
            </span>
          </div>

          {isPopupOpened && <div className={styles.categoriesPopupContent} ref={popupContentRef}>
              <CategoryTree onCategoryClick={() => setIsPopupOpened(false)} />
            </div>}

          {/* Linki nawigacyjne z CMS */}
          {navigationBarHtml && <div className={styles.cmsNavigationWrapper} dangerouslySetInnerHTML={{
          __html: navigationBarHtml
        }} />}
        </div>
      </Container>
    </div>;
};
export default HeaderBottomBar;